/**
 * Store para iluminación de fibras (estilo OZmap)
 * Permite resaltar visualmente rutas de fibras desde OLT hasta cliente
 * @module stores/useIlluminationStore
 */

import { create } from 'zustand'

export interface IlluminatedPath {
  cables: string[]
  fibras: string[]
  nodes: string[]
  sourceNodeId?: string
  targetNodeId?: string
  potenciaInicial?: number
  potenciaFinal?: number
  perdidaTotal?: number
}

interface IlluminationStore {
  illuminatedPath: IlluminatedPath | null
  isIlluminating: boolean
  
  setIlluminatedPath: (path: IlluminatedPath | null) => void
  clearIllumination: () => void
  
  addToPath: (cableId: string, fibraId?: string, nodeId?: string) => void
  
  isNodeIlluminated: (nodeId: string) => boolean
  isCableIlluminated: (cableId: string) => boolean
  isFibraIlluminated: (fibraId: string) => boolean
}

export const useIlluminationStore = create<IlluminationStore>((set, get) => ({
  illuminatedPath: null,
  isIlluminating: false,

  setIlluminatedPath: (path) => set({ 
    illuminatedPath: path, 
    isIlluminating: path !== null 
  }),

  clearIllumination: () => set({ 
    illuminatedPath: null, 
    isIlluminating: false 
  }),

  addToPath: (cableId, fibraId, nodeId) => {
    const current = get().illuminatedPath || { cables: [], fibras: [], nodes: [] }
    set({
      illuminatedPath: {
        ...current,
        cables: cableId && !current.cables.includes(cableId) 
          ? [...current.cables, cableId] 
          : current.cables,
        fibras: fibraId && !current.fibras.includes(fibraId) 
          ? [...current.fibras, fibraId] 
          : current.fibras,
        nodes: nodeId && !current.nodes.includes(nodeId) 
          ? [...current.nodes, nodeId] 
          : current.nodes,
      },
      isIlluminating: true,
    })
  },

  isNodeIlluminated: (nodeId) => {
    const path = get().illuminatedPath
    return path?.nodes?.includes(nodeId) ?? false
  },

  isCableIlluminated: (cableId) => {
    const path = get().illuminatedPath
    return path?.cables?.includes(cableId) ?? false
  },

  isFibraIlluminated: (fibraId) => {
    const path = get().illuminatedPath
    return path?.fibras?.includes(fibraId) ?? false
  },
}))
